<template>
  <div class="ly-f6" :class="{ 'is-min': folded }" @click="pop = false">
    <div class="ly-f6-head">
      <span class="ly-f6-title">Ф6 · {{ label }} · {{ bName }}</span>
      <button type="button" class="ly-f6-i" data-help="ф6/как считается" aria-label="как считается"
              :aria-expanded="String(pop)" @click.stop="pop = !pop">i</button>
      <button type="button" class="ly-f6-fold" data-help="ф6/свернуть" aria-label="свернуть"
              @click.stop="toggleFold">{{ folded ? '+' : '–' }}</button>
      <button type="button" class="ly-f6-x" data-help="ф6/закрыть" aria-label="закрыть" @click="$emit('close')">✕</button>
    </div>
    <template v-if="!folded">
      <div class="ly-f6-now">
        <span class="ly-f6-big">{{ lastI < 0 ? '—' : fmt(calc.f6[lastI]) }}</span>
        <span class="ly-f6-sub">{{ lastI < 0 ? 'мало данных для окна' : 'RMSSD ' + fmt(calc.base[lastI]) + ' · ' + dayLabel(series.days[lastI]) }}</span>
      </div>
      <svg :viewBox="`0 0 ${SW} ${SH}`" preserveAspectRatio="none">
        <rect v-if="spark.win" :x="spark.win.x" y="0" :width="spark.win.w" :height="SH" fill="var(--accent)" opacity="0.10" />
        <rect v-for="b in spark.bars" :key="b.i" :x="b.x" :y="b.y" width="2" :height="b.h" fill="var(--seq-40)" opacity="0.5" />
        <path :d="spark.base" fill="none" stroke="var(--text-muted)" stroke-width="1.2" stroke-dasharray="4 3" />
        <path :d="spark.f6" fill="none" stroke="var(--accent)" stroke-width="2" />
        <line x1="0" :x2="SW" :y1="SH - PB" :y2="SH - PB" stroke="var(--grid)" stroke-width="1" />
      </svg>
      <div v-for="r in ROWS" :key="r.k" class="ly-f6-row">
        <label :for="uid + r.k">{{ r.lab }}</label>
        <input :id="uid + r.k" type="range" :min="r.min" :max="r.max" :step="r.step" :value="p[r.k]"
               @input="p[r.k] = +$event.target.value">
        <span class="ly-f6-v">{{ num(p[r.k]) }}</span>
      </div>
      <div class="ly-f6-row">
        <label :for="uid + 'up'">только вверх</label>
        <input :id="uid + 'up'" v-model="p.up" type="checkbox">
      </div>
      <button type="button" class="ly-f6-reset" data-help="ф6/сброс" @click="reset">сбросить к базовым (15 · 8 · 0,1)</button>

      <div v-if="pop" class="ly-f6-pop" role="dialog" aria-label="как считается Ф6" @click.stop>
        <div class="ly-f6-pop-h"><b>Как считается — подробно</b>
          <button type="button" class="ly-f6-x" title="закрыть" @click="pop = false">✕</button></div>
        <div class="ly-f6-desc">
          <b>Ряд.</b> Для каждого календарного дня от первой до последней сессии столбца считается,
          сколько сессий дня ушли {{ metric === 'cum' ? 'в блоке не глубже' : 'ровно в блоке' }} <b>выбранного</b>,
          и делится на число сессий столбца. День без сессий = 0 — так в ряд попадает не только сила
          оттока, но и <b>частота</b> сессий. Выберите блок в срезе — ряд пересчитается по нему; без выбора
          считается последний блок.
          <ol>
            <li><b>База.</b> Скользящая RMSSD окна W дней:
              <code>RMSSDt = √( Σ(xi+1−xi)² / (n−1) )</code> — «дёрганость» ряда день-к-дню.</li>
            <li><b>Тренд.</b> Наклон Тейл—Сена (медиана попарных наклонов — устойчив к выбросам)
              базовой RMSSD за <b>T</b> дней, заканчивающихся <b>L</b> дней назад.</li>
            <li><b>Прогноз.</b> <code>Ft = RMSSDt−L + L·наклон</code> —
              где была бы вариабельность сегодня, если бы тренд того окна продолжился.</li>
            <li><b>Смешивание.</b> Если прогноз выше факта:
              <code>Ф6t = w·RMSSDt + (1−w)·Ft</code> — свежий хвост входит с весом всего w,
              и короткое затишье после пика не роняет линию. Иначе Ф6 = чистая RMSSD.</li>
            <li><b>«Только вверх».</b> Отрицательный наклон не вычитается: падение не прогнозируется,
              линия опускается не быстрее самой базы.</li>
          </ol>
          Зачем: обычная RMSSD забывает пик, как только он выходит из окна, и обваливается на 3–5 тихих
          днях — при редких сессиях это норма, а не сигнал. Ф6 держит линию тренда, пока накопленная
          история роста её оправдывает.
        </div>
      </div>

      <!-- дни недели: тот же разговор про ритм, поэтому живут в карточке Ф6 -->
      <div class="ly-wdc">
        <div class="ly-wdc-head">
          <span class="tlabel">по дням недели</span>
          <span class="ly-wdc-info">{{ units.length }} {{ plural(units.length, SES) }}<template v-if="range">
            · <b class="ly-f6-acc">в окне {{ wdInw }}</b></template></span>
        </div>
        <div class="ly-wdc-row">
          <div v-for="(c, i) in wdCols" :key="c.k" class="ly-wdc-col">
            <span class="ly-wdc-n" :class="{ 'is-dim': c.shown === 0 }">{{ c.shown }}<span v-if="c.part" class="ly-wdc-of">/{{ c.tot }}</span></span>
            <div class="ly-wdc-bar" :class="{ 'is-zero': c.tot === 0, 'is-rng': !!range }" :style="{ height: c.h + 'px' }"
                 @mouseenter="wdTip = c.k" @mouseleave="wdTip = null">
              <i v-if="range" class="ly-wdc-inw" :style="{ height: c.hw + 'px' }"></i>
            </div>
            <!-- своя всплывашка, как tip() в прототипе: полное имя дня жирным, ниже число -->
            <!-- у крайних столбцов всплывашка прижата к их краю, иначе «Вс · в окне» вылезает за карточку -->
            <span v-if="wdTip === c.k" class="ly-wdc-tip" :class="{ 'is-l': i < 2, 'is-r': i > wdCols.length - 3 }" role="tooltip"><b>{{ c.tipName }}</b>{{ c.tip }}</span>
            <span class="ly-wdc-lab" :class="{ 'is-we': c.k > 4 }">{{ WD[c.k] }}</span>
          </div>
        </div>
      </div>
      <div v-if="!series.vals.length" class="ly-f6-note">Ряд пуст: в столбце нет сессий с оттоком.</div>
      <div v-else class="ly-f6-note">
        Ряд: {{ dayLabel(series.minD) }} — {{ dayLabel(series.maxD) }}, {{ series.vals.length }}
        {{ plural(series.vals.length, DAYS) }}, из них с отмеченными сессиями {{ nz }}. Синие столбики — дневная доля сессий
        с оттоком {{ metric === 'cum' ? 'не глубже блока' : 'в блоке' }} «{{ bName }}»; пунктир — классическая RMSSD;
        сплошная — Ф6. Пустые дни считаются нулями — частота сессий входит в вариабельность.
      </div>
    </template>
  </div>
</template>

<script>
import { F6DEF, f6Series, f6Calc, weekdays } from '@/utils/f6';

// короткие месяцы, как dayLabel прототипа и даты отчёта A/B
const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const WD_FULL = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
const num = x => String(x).replace('.', ',');
// Окна расчёта переживают закрытие карточки: при повторном включении она вернётся такой же.
const saved = { ...F6DEF };
// Свёрнутость тоже: в прототипе панель одна на страницу и класс f6-min на ней остаётся.
const ui = { folded: false };
let uidN = 0;

export default {
  name: 'F6Card',
  props: {
    units: { type: Array, default: () => [] },
    block: { type: Number, default: null },
    metric: { type: String, default: 'cum' },
    ses: { type: Number, default: 0 },
    range: { default: null },                         // окно ленты времени или null — весь период
    label: { type: String, default: '' },
    nBlocks: { type: Number, default: 0 },           // число блоков воронки
  },
  emits: ['close'],
  data() {
    return {
      p: saved, folded: ui.folded, pop: false, wdTip: null, uid: 'ly-f6-' + (++uidN) + '-',
      SW: 268, SH: 86, PB: 12, PT: 6,
      WD: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
      DAYS: ['день', 'дня', 'дней'], SES: ['сессия', 'сессии', 'сессий'],
      ROWS: [
        { k: 'W', lab: 'окно RMSSD, дн.', min: 3, max: 14, step: 1 },
        { k: 'T', lab: 'окно тренда, дн.', min: 5, max: 25, step: 1 },
        { k: 'L', lab: 'лаг прогноза, дн.', min: 2, max: 12, step: 1 },
        { k: 'w', lab: 'вес хвоста', min: 0.05, max: 0.9, step: 0.05 },
      ],
    };
  },
  computed: {
    // без nBlocks f6Series возьмёт самый глубокий блок оттока в данных
    series() { return f6Series(this.units, this.block, this.metric, this.ses, this.nBlocks || undefined); },
    calc() { return f6Calc(this.series.vals, this.p); },
    bName() {
      const B = this.block;
      return B != null ? 'Блок ' + (B + 1) : 'итог';
    },
    lastI() {
      const f = this.calc.f6;
      for (let i = f.length - 1; i >= 0; i--) if (f[i] != null) return i;
      return -1;
    },
    nz() { return this.series.vals.filter(v => v > 0).length; },
    // спарклайн: столбики ряда, база пунктиром, Ф6 сплошной; окно ленты — подсветка
    spark() {
      const { vals, minD } = this.series, { base, f6 } = this.calc;
      const { SW, SH, PB, PT } = this, n = vals.length;
      const mxV = Math.max(1e-9, ...vals);
      const ms = [...f6, ...base].filter(v => v != null);
      const mxM = Math.max(0.001, ...ms);
      const X = i => 2 + i * (SW - 4) / Math.max(1, n - 1);
      // доли дня малы (≪ 1), поэтому столбики — от максимума ряда, а не от единицы, как в прототипе
      const Yv = v => SH - PB - v / mxV * (SH - PB - PT) * 0.55;
      const Ym = v => SH - PB - v / mxM * (SH - PB - PT);
      const path = (arr) => {
        let d = '', open = false;
        for (let i = 0; i < n; i++) {
          if (arr[i] == null) { open = false; continue; }
          d += (open ? 'L' : 'M') + X(i).toFixed(1) + ' ' + Ym(arr[i]).toFixed(1) + ' ';
          open = true;
        }
        return d;
      };
      let win = null;
      if (this.range && n) {
        const a = X(Math.max(0, this.range.from - minD)), b = X(Math.min(n - 1, this.range.to - minD));
        win = { x: a, w: Math.max(1, b - a) };
      }
      const bars = [];
      for (let i = 0; i < n; i++) if (vals[i] > 0) { const y = Yv(vals[i]); bars.push({ i, x: X(i) - 1, y, h: SH - PB - y }); }
      return { win, bars, base: path(base), f6: path(f6) };
    },
    wd() { return weekdays(this.units, this.range); },
    wdInw() { return this.wd.inw.reduce((a, b) => a + b, 0); },
    wdCols() {
      const { tot, inw } = this.wd, r = this.range;
      const mx = Math.max(1, ...tot), H = 40;        // карточка узкая, столбику хватает сорока пикселей
      return tot.map((t, k) => {
        const h = Math.round(t / mx * H);
        return { k, tot: t, shown: r ? inw[k] : t, part: !!r && inw[k] !== t,
          h: Math.max(2, h), hw: t ? Math.round(inw[k] / t * h) : 0,
          tipName: WD_FULL[k], tip: `${t} ${this.plural(t, this.SES)}${r ? ' · в окне ' + inw[k] : ''}` };
      });
    },
  },
  mounted() {
    // «как считается» закрывается Esc и кликом мимо карточки
    document.addEventListener('click', this.onDoc);
    document.addEventListener('keydown', this.onKey);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDoc);
    document.removeEventListener('keydown', this.onKey);
  },
  methods: {
    toggleFold() { this.folded = ui.folded = !this.folded; },
    num,
    // ряд — доли дня от ses столбца, обычно сотые: двух знаков мало, иначе почти всё «0,00»
    fmt(x) { return x == null ? '—' : x.toFixed(Math.abs(x) < 0.1 ? 3 : 2).replace('.', ','); },
    dayLabel(day) { const d = new Date(day * 864e5); return d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()]; },
    plural(n, f) {
      const a = Math.abs(n) % 100, b = a % 10;
      return a > 10 && a < 20 ? f[2] : b > 1 && b < 5 ? f[1] : b === 1 ? f[0] : f[2];
    },
    reset() { Object.assign(this.p, F6DEF); },
    onDoc(ev) { if (this.pop && !this.$el.contains(ev.target)) this.pop = false; },
    // preventDefault — знак сцене слоёв, что этот Esc уже потрачен на попап и из среза выходить не надо
    onKey(ev) { if (ev.key === 'Escape' && this.pop) { this.pop = false; ev.stopPropagation(); ev.preventDefault(); } },
  },
};
</script>

<style>
.ins-root .ly-f6{position:absolute; right:14px; top:10px; width:296px; z-index:60;
  background:var(--plate); backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px);
  border:1px solid var(--border); border-radius:14px; padding:12px 14px 10px;
  box-shadow:0 10px 30px rgba(0,0,0,.12); font-size:12px; color:var(--text-secondary);}
.ins-root .ly-f6.is-min{width:auto; padding:7px 12px;}
.ins-root .ly-f6-head{display:flex; align-items:center; gap:8px; margin-bottom:6px;}
.ins-root .ly-f6.is-min .ly-f6-head{margin-bottom:0;}
.ins-root .ly-f6-title{font-size:12.5px; font-weight:700; color:var(--text-primary); flex:1 1 auto;}
.ins-root .ly-f6-x{border:none; background:none; cursor:pointer; color:var(--text-muted); font-size:14px; padding:2px 4px;}
.ins-root .ly-f6-x:hover, .ins-root .ly-f6-fold:hover{color:var(--text-primary);}
.ins-root .ly-f6-fold{border:none; background:none; cursor:pointer; color:var(--text-muted); font-size:12px; padding:2px 4px;}
.ins-root .ly-f6-now{display:flex; gap:14px; align-items:baseline; margin:2px 0 6px; font-variant-numeric:tabular-nums;}
.ins-root .ly-f6-big{font-size:22px; font-weight:750; color:var(--accent);}
.ins-root .ly-f6-sub{font-size:11px; color:var(--text-muted);}
.ins-root .ly-f6 svg{display:block; width:100%; height:86px; margin:2px 0 8px;}
.ins-root .ly-f6-row{display:flex; align-items:center; gap:7px; margin:4px 0;}
.ins-root .ly-f6-row label{flex:0 0 96px; font-size:11px; line-height:1.2; margin:0;}
.ins-root .ly-f6-row input[type=range]{flex:1; min-width:40px; accent-color:var(--accent); height:14px;}
.ins-root .ly-f6-row input[type=checkbox]{accent-color:var(--accent);}
.ins-root .ly-f6-v{flex:0 0 34px; text-align:right; font-weight:650; color:var(--text-primary);
  font-variant-numeric:tabular-nums; font-size:11.5px;}
/* «i» — заметный кружок: объяснение расчёта живёт в нём */
.ins-root .ly-f6-i{width:18px; height:18px; flex:0 0 18px; border-radius:50%; cursor:pointer;
  border:1.5px solid var(--accent); background:none; color:var(--accent);
  font-size:11px; font-weight:800; line-height:1; padding:0; font-family:Georgia,serif;}
.ins-root .ly-f6-i:hover, .ins-root .ly-f6-i[aria-expanded="true"]{background:var(--accent); color:#fff;}
/* подпись как в прототипе (все пять значений — в плашке «ф6/сброс»): чип в одну строку, в заголовок плашки влезает целиком */
.ins-root .ly-f6-reset{margin-top:6px; border:1px solid var(--border); background:var(--surface-1); color:var(--text-muted);
  border-radius:999px; padding:5px 10px; font:inherit; font-size:12.5px; white-space:nowrap; cursor:pointer;}
.ins-root .ly-f6-reset:hover{color:var(--text-primary); border-color:var(--text-muted);}
.ins-root .ly-f6-acc{color:var(--accent);}
.ins-root .ly-f6-pop{position:absolute; right:calc(100% + 10px); top:0; width:340px; max-height:70vh;
  overflow:auto; z-index:70; background:var(--surface-1); border:1px solid var(--border);
  border-radius:12px; padding:10px 13px 12px; box-shadow:0 14px 40px rgba(0,0,0,.20);}
.ins-root .ly-f6-pop-h{display:flex; align-items:center; gap:8px; margin-bottom:4px; font-size:12.5px; color:var(--text-primary);}
.ins-root .ly-f6-pop-h b{flex:1 1 auto;}
.ins-root .ly-f6-desc{margin:6px 0 0; line-height:1.5; font-size:11.3px;}
.ins-root .ly-f6-desc b{color:var(--text-primary);}
.ins-root .ly-f6-desc ol{margin:4px 0 4px 16px; padding:0;}
.ins-root .ly-f6-desc li{margin:3px 0;}
.ins-root .ly-f6-desc code{font-family:ui-monospace,Menlo,monospace; font-size:10.6px; color:var(--text-primary);
  background:var(--surface-1); border:1px solid var(--border); border-radius:4px; padding:0 3px; white-space:nowrap;}
.ins-root .ly-f6-note{font-size:10.6px; color:var(--text-muted); margin-top:6px; line-height:1.45;}
@media (max-width:900px){
  .ins-root .ly-f6{width:250px;}
  .ins-root .ly-f6-pop{right:auto; left:0; top:calc(100% + 8px); width:290px;}
}
/* дни недели внутри карточки: рост и подписи ужаты под её ширину */
.ins-root .ly-wdc{margin:8px 0 0; border-top:1px solid var(--border); padding-top:7px;}
.ins-root .ly-wdc-head{display:flex; align-items:center; gap:6px; margin-bottom:6px; flex-wrap:wrap;}
.ins-root .ly-wdc-info{font-size:11px; color:var(--text-secondary); font-variant-numeric:tabular-nums;}
.ins-root .ly-wdc-row{display:flex; align-items:flex-end; gap:4px; height:58px;}
.ins-root .ly-wdc-col{position:relative; flex:1 1 0; display:flex; flex-direction:column; align-items:center; justify-content:flex-end;
  height:100%; min-width:0;}
.ins-root .ly-wdc-n{font-size:10.5px; font-weight:700; color:var(--text-primary); font-variant-numeric:tabular-nums; margin-bottom:2px;}
.ins-root .ly-wdc-n.is-dim{color:var(--text-muted); font-weight:400;}
.ins-root .ly-wdc-of{color:var(--text-muted); font-weight:400;}
.ins-root .ly-wdc-bar{width:80%; max-width:26px; border-radius:5px 5px 0 0; background:var(--seq-60);}
.ins-root .ly-wdc-bar.is-rng{background:var(--grid); position:relative; overflow:hidden; min-height:2px;}
.ins-root .ly-wdc-bar.is-zero{background:var(--grid);}
.ins-root .ly-wdc-inw{position:absolute; left:0; right:0; bottom:0; background:var(--accent);}
.ins-root .ly-wdc-lab{font-size:10.5px; color:var(--text-secondary); margin-top:4px; font-weight:650;}
.ins-root .ly-wdc-tip{position:absolute; bottom:calc(100% + 4px); left:50%; transform:translateX(-50%); z-index:5;
  background:var(--tooltip-bg); color:var(--tooltip-ink); border-radius:8px; padding:6px 9px; font-size:11.5px;
  white-space:nowrap; pointer-events:none; font-variant-numeric:tabular-nums; box-shadow:0 4px 14px var(--shadow-md);}
.ins-root .ly-wdc-tip.is-l{left:0; transform:none;}
.ins-root .ly-wdc-tip.is-r{left:auto; right:0; transform:none;}
.ins-root .ly-wdc-tip b{display:block; font-weight:650; margin-bottom:2px;}
.ins-root .ly-wdc-lab.is-we{color:var(--text-muted); font-weight:400;}
</style>
